<template>
  <div>
    <status
      :game-week="gameWeek"
      :is-loading="isLoading"
      :is-loading-button="isLoadingButton"
      :users-count="userMegaLeague?.usersCount"
      :status-confirmed="statusConfirmed"
      :should-confirm="shouldConfirm"
      :disabled="hasErrorCards"
      @confirm="putUserMegaLeague()"
      @cancel="deleteUserMegaLeague()"
    />
    <lineup
      v-if="lineup"
      :lineup="lineup"
      :players="players"
      :game-week="gameWeek"
      :forbidden-cards="usedCardsSlug"
      :error-cards="errorCards"
      show-average-score
      @formationChange="formationChange($event)"
      @cardPicked="cardPicked($event)"
      @captainSelected="captainSelected($event)"
      @cardRemoved="cardRemoved($event)"
    />
    <snackbar
      v-model="displaySnackbar"
      :text="snackbarText"
      :color="snackbarColor"
      :timeout="timeout"
    />
  </div>
</template>

<script>
import Status from '@/components/Lineup/Status.vue';
import Lineup from '@/components/Lineup/Lineup.vue';
import Snackbar from '@/components/Snackbar.vue';
import MegaLeagueService from '@/services/megaLeague.service';
import GameWeek from '@/models/GameWeek/gameWeek';

export default {
  name: 'MegaLeagueCreate',
  components: {
    Status,
    Lineup,
    Snackbar,
  },
  props: {
    gameWeek: {
      type: GameWeek,
      required: true,
    },
  },
  data() {
    return {
      userMegaLeague: null,
      usedCardsSlug: [],
      players: [],
      isLoading: false,
      isLoadingButton: false,
      statusConfirmed: false,
      shouldConfirm: true,
      displaySnackbar: false,
      snackbarText: null,
      snackbarColor: null,
      errorCards: [],
    };
  },
  computed: {
    lineup() {
      return this.userMegaLeague?.lineup;
    },
    hasErrorCards() {
      return this.errorCards.length > 0;
    },
    timeout() {
      return this.hasErrorCards ? -1 : 4000;
    },
  },
  watch: {
    $route() {
      this.getUserMegaLeague();
    },
    hasErrorCards(hasErrorCards) {
      this.displaySnackbar = hasErrorCards;
      this.snackbarColor = this.$consts.SNACKBAR.COLOR.ERROR;
      this.snackbarText = this.$consts.SNACKBAR.TEXT.MAX_TEAM;
    },
    gameWeek(newGameWeek) {
      this.getUserMegaLeague();
    },
  },
  mounted() {
    this.getUserMegaLeague();
  },
  methods: {
    getUserMegaLeague() {
      this.isLoading = true;
      MegaLeagueService.getUserMegaLeague(this.gameWeek.number)
        .then((response) => {
          this.userMegaLeague = response;
          this.statusConfirmed = this.userMegaLeague.lineup.isSet();
          this.shouldConfirm = !this.userMegaLeague.lineup.isSet();
          this.lineupUpdated();
        })
        .catch(() => {
          this.userMegaLeague = null;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    putUserMegaLeague() {
      this.isLoadingButton = true;
      MegaLeagueService.putUserMegaLeague(this.gameWeek.number, this.lineup.serialize())
        .then(() => {
          this.userMegaLeague.increaseUserCount();
          this.statusConfirmed = true;
          this.shouldConfirm = false;
          this.snackbarText = this.$consts.SNACKBAR.TEXT.TEAM_CONFIRMED;
          this.snackbarColor = this.$consts.SNACKBAR.COLOR.SUCCESS;
        })
        .catch((e) => {
          this.snackbarText = e.response?.data;
          this.snackbarColor = this.$consts.SNACKBAR.COLOR.ERROR;
        })
        .finally(() => {
          this.isLoadingButton = false;
          this.displaySnackbar = true;
        });
    },
    deleteUserMegaLeague() {
      this.isLoadingButton = true;
      MegaLeagueService.deleteUserMegaLeague(this.gameWeek.number)
        .then(() => {
          this.userMegaLeague.decreaseUserCount();
          this.userMegaLeague.lineup.reset();
          this.statusConfirmed = false;
          this.shouldConfirm = true;
          this.snackbarText = this.$consts.SNACKBAR.TEXT.TEAM_CANCELED;
          this.snackbarColor = this.$consts.SNACKBAR.COLOR.SUCCESS;
          this.lineupUpdated();
        })
        .finally(() => {
          this.isLoadingButton = false;
          this.displaySnackbar = true;
        });
    },
    formationChange(formation) {
      this.userMegaLeague.lineup.formation = formation;
      this.userMegaLeague.lineup.forwardsCard.length = this.userMegaLeague.lineup.forwardsCount();
      this.userMegaLeague.lineup.midfieldersCard.length = this.userMegaLeague.lineup.midfieldersCount();
      this.userMegaLeague.lineup.defendersCard.length = this.userMegaLeague.lineup.defendersCount();
      this.lineupUpdated();
    },
    cardPicked(data) {
      this.shouldConfirm = true;
      this.userMegaLeague.lineup[data.position][data.index] = data.card;
      this.lineupUpdated();
    },
    cardRemoved(data) {
      if (this.userMegaLeague.lineup[data.position][data.index].slug === this.userMegaLeague.lineup.captain) {
        this.userMegaLeague.lineup.captain = null;
      }

      delete this.userMegaLeague.lineup[data.position][data.index];
      this.lineupUpdated();
    },
    captainSelected(cardSlug) {
      this.shouldConfirm = true;
      this.userMegaLeague.lineup.captain = cardSlug;
    },
    lineupUpdated(){
      this.updatePlayers();
      this.computeErrorCards()
    },
    updatePlayers() {
      this.players = this.userMegaLeague?.lineup.getCards().map((card) => card.player);
    },
    computeErrorCards() {
      if (!this.lineup) return [];
      const errorCards = [];
      const allCards = this.lineup.getCards();
      const teamCards = {};

      allCards.forEach((card) => {
        const player = card.player;
        const clubName = player?.club?.name;
        const nationalTeamName = player?.nationalTeam?.name;
        const cardHomeName = card.fixture?.homeTeam.name;
        const cardAwayName = card.fixture?.awayTeam.name;
        let key = null;

        if (
          (typeof cardHomeName === 'string' || typeof cardAwayName === 'string') &&
          (
            cardHomeName === clubName
            || cardAwayName === clubName
            || cardHomeName === nationalTeamName
            || cardAwayName === nationalTeamName
          )
        ) {
          if (cardHomeName === clubName || cardHomeName === nationalTeamName) {
            key = cardHomeName;
          } else {
            key = cardAwayName;
          }

          if (!teamCards[key]) {
            teamCards[key] = [];
          }

          teamCards[key].push(card);
        }
      });

      Object.values(teamCards).forEach((cards) => {
        if (cards.length > 3) {
          errorCards.push(...cards);
        }
      });

      this.errorCards = errorCards;
    }
  },
};
</script>

<style scoped lang="scss">
.status {
  margin: auto;
}
</style>

